import * as React from 'react';

import VisuallyHidden from '@core/VisuallyHidden';

type AddAnnoucement = (message: string) => void;
type A11yAnnouncerProviderContext = {
  /** Write a message that only screen readers will see */
  addAnnouncement?: AddAnnoucement;
};

const ARIA_LIVE_TIME_MS = 500;
const A11yAnnouncerContext = React.createContext<A11yAnnouncerProviderContext>({
  addAnnouncement: undefined,
});

type Props = {
  children?: React.ReactNode;
};

/**
 * Lives at the top of the children components, providing a location to
 * broadcast live updates to a screen reader-using user.
 */
export const A11yAnnouncerProvider = ({ children }: Props) => {
  const [message, setMessage] = React.useState('');
  const [timeoutVar, setTimeoutVar] = React.useState<number>();

  const addAnnouncement = React.useCallback<AddAnnoucement>(
    (newAnnouncement) => {
      clearTimeout(timeoutVar);
      setMessage(newAnnouncement);
      const timeout = window.setTimeout(() => {
        setMessage('');
      }, ARIA_LIVE_TIME_MS);
      setTimeoutVar(timeout);
    },
    [timeoutVar]
  );

  return (
    <A11yAnnouncerContext.Provider
      value={{
        addAnnouncement,
      }}
    >
      <VisuallyHidden aria-live="assertive" role="status">
        {message}
      </VisuallyHidden>
      {children}
    </A11yAnnouncerContext.Provider>
  );
};

export const useA11yAnnouncer = () => React.useContext(A11yAnnouncerContext);
